/** @jsx jsx */
// @ts-check
import { Flex } from '@bottlebooks/gatsby-theme-base';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import { getFluidImage } from '../../helpers/getImageProps';
import BrandLogo from '../Brand/BrandLogo';
import OfferingBadge from '../Brand/OfferingBadge';
import ExhibitorHeader from '../Exhibitor/ExhibitorHeader';
import ListRow from '../List/ListRow';
import ListRowBackgroundImage from '../List/ListRowBackgroundImage';
import ListRowBody from '../List/ListRowBody';

export default function ExhibitorListRow({ exhibitor, children, ...rest }) {
  const mainImage = exhibitor?.mainImage?.fluid;
  const isPro = exhibitor?.isPro;
  const hasBackground = Boolean(mainImage) && isPro;
  return (
    <ListRow
      {...rest}
      onDark={hasBackground}
      sx={{
        minHeight: 200,
        flexDirection: ['column', 'row'],
        boxSizing: 'border-box',

        // Only add hover to links. The `&` is replaced by the class name automatically.
        'a[href]&:hover': {
          ' > .bottlebooks-bgimg': {
            transform: 'scale(1.01)',
            '::after': {
              backgroundColor: 'overlaySecondary',
            },
          },
        },
      }}
    >
      {hasBackground && (
        <ListRowBackgroundImage
          fluid={getFluidImage(mainImage)}
          className="bottlebooks-bgimg"
        />
      )}
      <BrandLogo
        brand={exhibitor}
        onDark={hasBackground}
        sx={{
          margin: 3,
          marginRight: 0,
          position: 'relative',
        }}
      />
      <ListRowBody sx={{ position: 'relative', marginBottom: 3 }}>
        <ExhibitorHeader exhibitor={exhibitor} onDark={hasBackground} />
      </ListRowBody>
      <BrandFeatures brand={exhibitor} />

      {children}
    </ListRow>
  );
}

export function BrandFeatures({ brand }) {
  if (!brand.offeringTypes?.length) return null;
  return (
    <Flex
      justify="flex-end"
      align="flex-end"
      gap={2}
      sx={{
        width: '100%',
        paddingRight: 2,
        paddingBottom: 2,
        position: 'relative', // Needed to stay above image backgrounds
        marginLeft: 'auto',
      }}
    >
      {brand.offeringTypes?.map((type) => (
        <OfferingBadge key={type} variant="onPrimary" type={type} />
      ))}
    </Flex>
  );
}

export const fragment = graphql`
  fragment ExhibitorListRow on Exhibitor {
    isPro
    ...BrandLogoSmall_list
    ...ExhibitorHeader
    offeringTypes
    mainImage {
      fluid(maxWidth: 700, maxHeight: 200, crop: FILL, gravity: FACES_AUTO) {
        src
        sizes
        aspectRatio
      }
    }
  }

  # A fragment for the first page of exhibitors. It has more detailed images.
  fragment ExhibitorListRow_first on Exhibitor {
    ...BrandLogoSmall
    mainImage {
      fluid(maxWidth: 700, maxHeight: 200, crop: FILL, gravity: FACES_AUTO) {
        src
        srcSet
        sizes
        base64
        aspectRatio
      }
    }
  }
`;
